import { tWithKeyAttr as t } from "../../../i18nHelper";
import styles from "./addNewUserForm.module.css";
import { useFormik } from "formik";
import { addNewUserValidationSchema } from "../../../utils/validation";
import React, { useEffect, useState } from "react";
import {
  useAddUserMutation,
  useGetAllCitiesQuery,
  useGetTreatmentGroupsQuery,
  useLazyGetGendersQuery,
  useLazyGetUsersQuery,
} from "../../../services/api/user.api";
import Select, { ActionMeta, SingleValue } from "react-select";

import { useAppSelector } from "../../../services/hooks";
import {
  gendersSelector,
  userSelector,
} from "../../../services/selectors/user.selectors";
import Modal from "../../modals/modal/Modal";
import ErrorPopup from "../../modals/errorPopup/errorPopup";
import { addUserInitialState, statusOptions } from "../../../utils/constants";
import GeneralSuccessPopup from "../../modals/generalSuccessPopup/generalSuccessPopup";
import ChangePasswordPopup from "../../modals/changePasswordPopup/changePasswordPopup";
import { TAddUserRequest } from "../../../types/types";
import { TTherapistUser } from "../../../services/types/user.types";
import { convertKeysToLowerCase } from "../../../utils/utils";
import CloseButton from "../../closeButton/closeButton.module";
import i18n from "../../../i18n";

function AddNewUserForm({
  onClose,
  user,
  defaultPhone,
}: {
  onClose: () => void;
  user?: TTherapistUser | undefined;
  /** Prefill phone when opening “add user” without an existing therapist user row (e.g. unidentified MVoice caller). */
  defaultPhone?: string;
}) {
  const therapist = useAppSelector(userSelector);
  const genders = useAppSelector(gendersSelector)?.map(
    (item: { id: number; name: string }) => ({
      ...item,
      label: item.name,
      value: item.id,
    })
  );

  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState<string | undefined>(
    undefined
  );
  const [addUser] = useAddUserMutation();
  const [getAllGenders] = useLazyGetGendersQuery();
  const [getAllTherapistUsers] = useLazyGetUsersQuery();

  const { data: treatmentGroupsData } = useGetTreatmentGroupsQuery(
    { companyId: therapist.companyId },
    { skip: !therapist.companyId }
  );

  const { data: citiesData } = useGetAllCitiesQuery();

  useEffect(() => {
    if (!genders?.length) {
      getAllGenders();
    }
  }, [genders, getAllGenders]);

  const formik = useFormik<TAddUserRequest>({
    initialValues: {
      ...addUserInitialState,
      fakeCompanyId: therapist.fakeCompanyId, //for new users
      roleId: 1,
      ...(user ? { ...convertKeysToLowerCase(user) } : {}), // Spread the user object if it exists
      ...(!user && defaultPhone?.trim()
        ? { phone: defaultPhone.trim() }
        : {}),
      companyId: therapist.companyId,
    },
    validationSchema: addNewUserValidationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting }) => {
      const { password, roles, ...rest } = values;
      const body = !values.id ? values : rest;
      // Handle form submission
      addUser({
        companyId: therapist.companyId,
        body,
      })
        .then((res) => {
          if (res && "data" in res) {
            if (res.data && "desc" in res.data) {
              setOpenErrorModal(res.data.desc);
            } else if (res.data) {
              setOpenSuccessModal(true);
            }
          } else if (res && "error" in res) {
            setOpenErrorModal(t('MAIN_PAGE_TREATMENT_ERROR'));
          }
        })
        .finally(() => {
          getAllTherapistUsers();
          setSubmitting(false);
        });
    },
  });

  const onSelectChange = (
    value: SingleValue<{ id: number; name: string; value: number; label: string }>,
    { name }: ActionMeta<{ id: number; name: string; value: number; label: string }>
  ) => {
    if (value && name) {
      formik.setFieldValue(name, value.value);
    }
  };

  const isDisabled =
    JSON.stringify(formik.values) === JSON.stringify(formik.initialValues);

  const localizedStatusOptions = React.useMemo(
    () =>
      statusOptions.map((opt) => ({
        ...opt,
        label:
          i18n.language && i18n.language.startsWith("en")
            ? opt.value === "1"
              ? "Active"
              : "Inactive"
            : opt.label,
      })),
    [i18n.language]
  );

  return (
    <form dir={i18n.dir()}
      className={`${styles.form} ${styles.form_type_addUser}`}
      onSubmit={formik.handleSubmit}
    >
      {!user && <div style={{ position: "relative" }}>
        <div
          className={`${styles.clsbtn}`}

        >
          <CloseButton onClick={onClose} />
        </div>
      </div>}
      <h2 className={`title title_blue`}>
        {user ? t("EDIT_USER", { id: user.Id }) : t("ADD_USER")}
      </h2>
      <ul
        className={`${styles.form__list} ${styles.form__list_type_fourColumns} `}
      >
        <li>
          <label htmlFor="firstName" className={styles.form__label}>{t('PROFILE_FIRST_NAME')}</label>
          <input
            type="text"
            className={`${styles.form__input} ${formik.errors.firstName && styles.form__input_invalid
              }`}
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={t('PROFILE_FIRST_NAME')}
            autoFocus={true}
          />
        </li>
        <li>
          <label htmlFor="lastName" className={styles.form__label}>{t('PROFILE_LAST_NAME')}</label>
          <input
            type="text"
            className={`${styles.form__input} ${formik.errors.lastName && styles.form__input_invalid
              }`}
            name="lastName"
            id="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={t('PROFILE_LAST_NAME')}
          />
        </li>
        <li>
          <label htmlFor="guardianName" className={styles.form__label}>Guardian Name (Optional)</label>
          <input
            type="text"
            className={`${styles.form__input} ${(formik.errors as any).guardianName && styles.form__input_invalid
              }`}
            name="guardianName"
            value={(formik.values as any).guardianName || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter guardian name"
          />
        </li>
        <li>
          <label htmlFor="email" className={styles.form__label}>{t('LOGIN_PAGE_WAY_EMAIL')}</label>
          <input
            type="string"
            className={`${styles.form__input} ${formik.errors.email && styles.form__input_invalid
              }`}
            name="email"
            id="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={t('LOGIN_PAGE_WAY_EMAIL')}
          />
        </li>
        <li>
          <label htmlFor="passport" className={styles.form__label}>
            {t("ID_NUMBER")}
          </label>
          <input
            type="string"
            className={`${styles.form__input} ${formik.errors.passport && styles.form__input_invalid
              }`}
            name="passport"
            id="passport"
            value={formik.values.passport}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={t("ID_NUMBER")}
          />
        </li>
        <li>
          <label htmlFor="birth" className={styles.form__label}>
            {t("BIRTH_DATE")}
          </label>
          <input
            type="date"
            className={`${styles.form__input} ${formik.errors.birth && styles.form__input_invalid
              }`}
            name="birth"
            id="birth"
            value={formik.values.birth ? formik.values.birth.split("T")[0] : ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={t("BIRTH_DATE")}
          />
        </li>
        <li>
          <label htmlFor="phone" className={styles.form__label}>
            {t("MOBILE_PHONE")}
          </label>
          <input
            type="tel"
            className={`${styles.form__input} ${styles.form__input_type_phone} ${formik.errors.phone && styles.form__input_invalid
              }`}
            name="phone"
            value={formik.values.phone}
            onChange={(e) => {
              const sanitized = e.target.value.replace(/[^+\d]/g, "");
              if (/^\+?\d*$/.test(sanitized) && sanitized.length <= 16) {
                formik.setFieldValue("phone", sanitized);
              }
            }}
            onBlur={formik.handleBlur}
            placeholder={t("MOBILE_PHONE")}
          />
        </li>
        <li>
          <label htmlFor="genderId" className={styles.form__label}>{t('PROFILE_GENDER')}</label>
          <Select
            name={"genderId"}
            options={genders}
            value={genders?.find((o) => o.value === formik.values.genderId)}
            placeholder={t('ADD_TREATMENT_PLAN_PLACEHOLDER_SELECT')}
            className={styles.select}
            onChange={onSelectChange}
          />
        </li>
        <li>
          <label htmlFor="statusId" className={styles.form__label}>{t('ADD_TREATMENT_PLAN_LABEL_STATUS')}</label>
          <Select
            name={"statusId"}
            options={localizedStatusOptions}
            value={localizedStatusOptions.find(
              (opt) => opt.value === formik.values.statusId?.toString()
            )}
            placeholder={t('ADD_TREATMENT_PLAN_PLACEHOLDER_SELECT_STATUS')}
            className={styles.select}
            onChange={(value) => {
              value && formik.setFieldValue("statusId", value.value);
            }}
          />
        </li>
        <li>
          <label htmlFor="treatmentGroupId" className={styles.form__label}>{t('ASSIGN_TO_GROUP')}</label>
          <Select<{ value: number; label: string }>
            name="treatmentGroupId"
            inputId="treatmentGroupId"
            options={treatmentGroupsData?.filter(g =>
              g.statusId === 1 && (!g.isFull || g.id === formik.values.treatmentGroupId)
            ).map(group => ({
              value: group.id,
              label: `${group.groupName}${group.maxParticipants ? ` (${group.currentMemberCount || 0}/${group.maxParticipants})` : ' (אין הגבלה)'}`,
            })) || []}
            value={
              formik.values.treatmentGroupId
                ? treatmentGroupsData
                  ?.map(group => ({
                    value: group.id,
                    label: `${group.groupName}${group.maxParticipants ? ` (${group.currentMemberCount || 0}/${group.maxParticipants})` : ' (אין הגבלה)'}`,
                  }))
                  .find(opt => opt.value === formik.values.treatmentGroupId) || null
                : null
            }
            placeholder={t('SELECT_TREATMENT_GROUP_OPTIONAL')}
            className={styles.select}
            isClearable
            onChange={(value: SingleValue<{ value: number; label: string }>) => {
              formik.setFieldValue("treatmentGroupId", value?.value || null);
            }}
          />
        </li>
        <li>
          <label htmlFor="cityId" className={styles.form__label}>{t('CITY')}</label>
          <Select
            name="cityId"
            inputId="cityId"
            options={citiesData?.map((city: { id: number; name: string }) => ({
              value: city.id,
              label: city.name,
            })) || []}
            value={citiesData?.find((city: { id: number; name: string }) => city.id === formik.values.cityId) ? {
              value: formik.values.cityId,
              label: citiesData.find((city: { id: number; name: string }) => city.id === formik.values.cityId)?.name || '',
            } : null}
            placeholder={`${t('CHOOSE')} ${t('CITY')}`}
            className={styles.select}
            isClearable
            onChange={(value) => {
              formik.setFieldValue("cityId", value?.value || null);
            }}
          />
        </li>
        <li>
          <label htmlFor="address" className={styles.form__label}>{t('ADDRESS')}</label>
          <input
            type="text"
            className={styles.form__input}
            name="address"
            id="address"
            data-testid="address"
            value={formik.values.address || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={t('ADDRESS')}
          />
        </li>
        <li>
          <span
            className={`${styles.form__label} underlined pointer`}
            onClick={() => setOpenPasswordModal(true)}
          >{t('PROFILE_EDIT_PASSWORD')}</span>
          <input
            type="password"
            className={`${styles.form__input} ${formik.errors.password && styles.form__input_invalid
              }`}
            name="password"
            id="password"
            value={formik.values.password || ""}
            onBlur={formik.handleBlur}
            readOnly
            onClick={() => setOpenPasswordModal(true)}
          />
        </li>
      </ul>

      {formik.errors && (
        <p className={`error ${styles.signin_private__invalid_message}`}>
          {t(formik.errors.firstName ??
            formik.errors.lastName ??
            formik.errors.email ??
            formik.errors.passport ??
            formik.errors.birth ??
            formik.errors.phone ??
            formik.errors.password)}
        </p>
      )}
      <button
        type="submit"
        disabled={!formik.isValid || formik.isSubmitting || isDisabled}
        className={`button ${styles.form__btn} ${!formik.isValid || formik.isSubmitting || isDisabled
          ? styles.form__btn_disabled
          : "hover"
          }`}
      >
        {t('CONTINUE')}</button>
      {openSuccessModal && (
        <Modal
          onClose={() => {
            setOpenSuccessModal(false);
            onClose();
          }}
        >
          <GeneralSuccessPopup
            onClose={() => {
              setOpenSuccessModal(false);
              onClose();
            }}
          />
        </Modal>
      )}
      {openErrorModal && (
        <Modal
          onClose={() => {
            setOpenErrorModal(undefined);
          }}
        >
          <ErrorPopup
            text={openErrorModal}
            onClose={() => {
              setOpenErrorModal(undefined);
            }}
          />
        </Modal>
      )}
      {openPasswordModal && (
        <Modal onClose={() => setOpenPasswordModal(false)}>
          <ChangePasswordPopup
            onClose={() => setOpenPasswordModal(false)}
            targetUserGuid={formik.values.id ? formik.values.guid : undefined}
            onNewPassword={!formik.values.id ? (password) => formik.setFieldValue("password", password) : undefined}
          />
        </Modal>
      )}
    </form>
  );
}

export default AddNewUserForm;
